import { Box, IconButton, makeStyles } from "@material-ui/core";
import { SettingsOutlined } from "@material-ui/icons";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import React, { FC, useState } from "react";
import { useHistory } from "react-router";
import { useRecoilValueLoadable, useRecoilValue, useSetRecoilState } from "recoil";

// MEMO: プロフィール充実促進は一旦無効化
// import EditProfileIcon from "../../../../assets/image/edit_profile_icon.png";
import { MsinsLeadEntrySourceEnum, ScreenNameEnum, User } from "@/api/generated/api";
import { Loading } from "@/components/Loading";
import { LeadCheckModal } from "@/components/management/LeadCheckModal";
import { MsinsLeadModal } from "@/components/management/MsinsLeadModal";
import { OfferApproveModal } from "@/components/offer/ApproveModal";
import { AddNewPropertyModal } from "@/components/property/AddNewPropertyModal";
import { LimitedPropertiesSection, LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { layoutTokens } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useGetLimitedProperties } from "@/hooks/useGetLimitedProperties";
import { usePatchUser } from "@/hooks/usePatchUser";
import { usePostMsinsLead } from "@/hooks/usePostMsinsLead";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { NavigationPath } from "@/utilities/enums";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";
import { BackendLoadedState, UserState } from "@/view_models/atoms";
import { LoanInformationOnToday, PropertyIdToThisMonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";
import { HasOwnedProperties, OwnedPropertiesWithSample } from "@/view_models/propertySelectors";

// MEMO: プロフィール充実促進は一旦無効化
// import { calculateBasicInfoCompletionRateFromUser } from "../settings/components/items/BasicInfo";
// MEMO: 初回登録時機能アップデート表示停止のためモーダルコンポーネントを一時的に無効化
// import AnnouncementModal from "./components/AnnouncementModal";
import AnnouncementSection from "./components/AnnouncementSection";
import { CashFlowSection, EmptyCashFlowSection } from "./components/CashFlowSection";
import { CompatibilityAgentsSection, CompatibilityAgentsSectionSkeleton } from "./components/CompatibilityAgentsSection";
import { CozuchiModal } from "./components/CozuchiModal";
import { HomeMobileSections } from "./components/HomeMobileSections";
import { HomeTour } from "./components/HomeTour";
import { InvestmentStyleSummarySection } from "./components/InvestmentStyleSummarySection";
import { LearningSection } from "./components/LearningSection";
import { MatchingAgentSection } from "./components/MatchingAgentSection";
import { NewNotificationSection } from "./components/NewNotificationSection";
import NotificationPermissionModal from "./components/NotificationPermissionModal";
import { PremiumWebinarSection } from "./components/PremiumWebinarSection";
import SecondOpinionStartupModal from "./components/SecondOpinionStartupModal";
import SmaulStartupModal from "./components/SmaulStartupModal";
import StartupProfileModal, { StartupProfileUpdate } from "./components/StartupProfileModal";
// MEMO: 初回登録時機能アップデート表示停止のためアナウンスフックを一時的に無効化
// import { useAnnouncement } from "./hooks/useAnnouncement";
import { useGetProposalMatchings } from "./hooks/useGetProposalMatchings";
import { useHomeCompatibilityAgents } from "./hooks/useHomeCompatibilityAgents";
import { useHomeDisplayData } from "./hooks/useHomeDisplayData";
import { useHomeInvestmentStyleDiagnosis } from "./hooks/useHomeInvestmentStyleDiagnosis";
import { useMyPageInitialization } from "./hooks/useInitialization";
import { useNotificationPermissionModal } from "./hooks/useNotificationPermissionModal";
import { useOpenUsageGuide } from "./hooks/useOpenUsageGuide";
import { usePostUserAccessScreenLogs } from "./hooks/usePostUserAccessScreenLogs";
import { usePremiumWebinarHighlights } from "./hooks/usePremiumWebinarHighlights";
import { HomeSectionKey, buildHomeSectionOrder, splitHomeSectionOrderForPc } from "./utils/homeSectionOrder";
import { canShowStyleDiagnosisSection } from "./utils/homeSectionVisibility";
import { StartupPopupManager } from "./utils/startupPopupManager";
import { getMissingStartupProfileFields, shouldShowStartupProfileModal } from "./utils/startupProfileModalLogic";

const CAMPAIGN_GOOGLE_FORM_BASE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSdBCQ8tlIsgt-jrHRk0c8sazSTNNEJ_OXawdThgPfaaRVIs9w/viewform?usp=pp_url";
const SECOND_OPINION_GOOGLE_FORM_BASE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSeNaFR0sY3D_gg7ifSv6QHFomJzpalKgXvTXsudRq69IDx0ig/viewform?usp=pp_url";
const SMAUL_GOOGLE_FORM_BASE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSeVFgUvE8dUUo51eYpAQV1L3ArcKqvLWPtv6Cs6RawkChDvhw/viewform?usp=pp_url";

// MEMO: 技術的には全データを送れるが、セキュリティを鑑み、ユーザ識別子のみformに送る
const buildCampaignGoogleFormPrefilledUrl = (user: User | null) => {
  const params = new URLSearchParams();
  // const fullName = [user?.family_name ?? "", user?.first_name ?? ""].filter((value) => value).join(" ");

  // params.set("entry.1234567890", fullName);
  // params.set("entry.1234567890", user?.phone_number ?? "");
  // params.set("entry.1234567890", user?.email ?? "");
  params.set("entry.876993874", user?.identifier ?? "");

  const query = params.toString();
  return query ? `${CAMPAIGN_GOOGLE_FORM_BASE_URL}&${query}` : CAMPAIGN_GOOGLE_FORM_BASE_URL;
};

const buildSecondOpinionGoogleFormPrefilledUrl = (user: User | null) => {
  const params = new URLSearchParams();
  params.set("entry.876993874", user?.identifier ?? "");

  const query = params.toString();
  return query ? `${SECOND_OPINION_GOOGLE_FORM_BASE_URL}&${query}` : SECOND_OPINION_GOOGLE_FORM_BASE_URL;
};

const buildSmaulGoogleFormPrefilledUrl = (user: User | null) => {
  const params = new URLSearchParams();
  params.set("entry.1655835726", user?.identifier ?? "");

  const query = params.toString();
  return query ? `${SMAUL_GOOGLE_FORM_BASE_URL}&${query}` : SMAUL_GOOGLE_FORM_BASE_URL;
};

const useStyles = makeStyles(() => ({
  chipWrapper: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  chip: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0, 3),
    "&:hover, &:active": {
      backgroundColor: theme.palette.info.dark,
    },
    "&:focus": {
      backgroundColor: theme.palette.info.main,
    },
  },
  chipIcon: {
    color: theme.palette.common.white,
  },
  icon: {
    width: "44px",
    height: "44px",
  },
  iconImage: {
    width: "40px",
    height: "40px",
  },
  sectionStack: {
    display: "flex", flexDirection: "column", gap: layoutTokens.space[32],
    padding: `${layoutTokens.space[32]}px ${layoutTokens.space[24]}px`,
    [theme.breakpoints.up("md")]: { padding: `${layoutTokens.space[32]}px 0` },
  },
  pcColumns: { display: "flex", alignItems: "flex-start", gap: layoutTokens.space[40], width: "100%" },
  pcMainColumn: { width: 384, maxWidth: "32%", flexShrink: 0, display: "flex", flexDirection: "column", gap: layoutTokens.space[24], minWidth: 0 },
  pcRightRail: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: layoutTokens.space[40] },
  // 他社サービスとコラボを行なった際の過去実装
  // floatingBanner: {
  //   position: "fixed",
  //   bottom: theme.spacing(2),
  //   right: theme.spacing(2),
  //   width: "400px",
  //   zIndex: 1000,
  // },
  // closeButton: {
  //   color: theme.palette.common.white,
  //   backgroundColor: theme.palette.grey[600],
  //   position: "absolute",
  //   top: "-16px",
  //   right: "-16px",
  //   width: "32px",
  //   height: "32px",
  //   "&:hover": {
  //     backgroundColor: theme.palette.grey[500],
  //   },
  // },
  profileBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    borderRadius: theme.spacing(1),
    padding: "10px 16px",
    cursor: "pointer",
    boxSizing: "border-box",
    width: "100%",
    color: theme.palette.tertiary.contrastText,
    textDecoration: "none",
  },
  profileBannerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  profileBannerIcon: {
    color: theme.palette.tertiary.contrastText,
    width: 30,
    height: 30,
  },
  profileBannerTexts: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  profileBannerTextPrimary: {
    color: theme.palette.tertiary.contrastText,
    fontWeight: 700,
    lineHeight: "150%",
    letterSpacing: "0.7px",
  },
  profileBannerTextSecondary: {
    color: theme.palette.tertiary.contrastText,
    fontWeight: 400,
    lineHeight: "150%",
    letterSpacing: "0.6px",
  },
  profileBannerAction: {
    backgroundColor: theme.palette.tertiary.contrastText,
    color: theme.palette.primary.main,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1),
    fontWeight: 700,
    lineHeight: "150%",
    letterSpacing: "0.6px",
    whiteSpace: "nowrap",
  },
  editProfileIcon: {
    height: "30px",
  },
}));

export const MypagePage: FC = () => {
  const [isAddPropertyModalOpen, setIsAddPropertyModalOpen] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const { isMobile } = useDevice();
  const user = useRecoilValue(UserState);
  const setUser = useSetRecoilState(UserState);

  const loan_loadable = useRecoilValueLoadable(LoanInformationOnToday);
  const property_id_to_this_month_sheet_loadable = useRecoilValueLoadable(PropertyIdToThisMonthlyBalanceSheet(null));
  const backendLoaded = useRecoilValue(BackendLoadedState);

  const propertiesWithSample = useRecoilValue(OwnedPropertiesWithSample);
  const hasOwnedProperties = useRecoilValue(HasOwnedProperties);

  const { enqueueSnackbar } = useSnackbar();
  const { postMsinsLead } = usePostMsinsLead();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isMsinsLeadModalOpen, setIsMsinsLeadModalOpen] = useState(false);
  const [isMsinsCheckModalOpen, setIsMsinsCheckModalOpen] = useState(false);
  const [isSecondOpinionStartupModalOpen, setIsSecondOpinionStartupModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileSubmitAttempted, setProfileSubmitAttempted] = useState(false);
  const [pendingProfileUpdate, setPendingProfileUpdate] = useState<StartupProfileUpdate | null>(null);
  const [profileErrorMessage, setProfileErrorMessage] = useState("");
  const [isSmaulStartupModalOpen, setIsSmaulStartupModalOpen] = useState(false);
  const [isCozuchiModalOpen, setIsCozuchiModalOpen] = useState(false);
  const limitedPropertiesResponse = useGetLimitedProperties();

  // 他社サービスとコラボを行なった際の過去実装
  // const [showBanner, setShowBanner] = React.useState(true);

  useMyPageInitialization();
  usePostUserAccessScreenLogs(ScreenNameEnum.Top);

  const handleOpenModal = useOpenUsageGuide();
  const proposalMatchingsResponse = useGetProposalMatchings();
  const { webinars: premiumWebinars, loading: premiumWebinarsLoading } = usePremiumWebinarHighlights();
  // 診断状態はサーバフラグに頼らず、常に GET で確認する（フラグは実データと乖離しうるため）
  const investmentStyleResponse = useHomeInvestmentStyleDiagnosis();
  // 相性スコアは登録時に必須の投資力診断で算出されるため、投資スタイル診断の状態を待たずに取得する
  const compatibilityAgentsResponse = useHomeCompatibilityAgents();
  const {
    investmentStyle: { status: investmentStyleStatus, diagnosis: investmentStyleDiagnosis },
    proposalMatchings, proposalMatchingsLoading, compatibilityAgents, compatibilityAgentsLoading,
    limitedProperties, limitedLoading,
  } = useHomeDisplayData({
    investmentStyle: investmentStyleResponse,
    proposalMatchings: proposalMatchingsResponse.proposalMatchings,
    proposalMatchingsLoading: proposalMatchingsResponse.loading,
    compatibilityAgents: compatibilityAgentsResponse.agents,
    compatibilityAgentsLoading: compatibilityAgentsResponse.loading,
    limitedProperties: limitedPropertiesResponse.limitedProperties,
    limitedLoading: limitedPropertiesResponse.loading,
  });
  const topLimitedProperties = React.useMemo(() => limitedProperties.slice(0, 5), [limitedProperties]);
  const {
    checking: isNotificationPermissionChecking,
    open: isNotificationModalOpen,
    requesting: isRequestingNotificationPermission,
    requestPermission: requestNotificationPermission,
    handleClose: closeNotificationPermissionModal,
  } = useNotificationPermissionModal();
  const {
    patchUser: patchProfileUser,
    response: patchProfileResponse,
    loading: patchProfileLoading,
    error: patchProfileError,
  } = usePatchUser();
  const missingStartupProfileFields = React.useMemo(() => getMissingStartupProfileFields(user), [user]);
  const hasEvaluatedStartupPopup = React.useRef(false);
  const [startupPopupEvaluated, setStartupPopupEvaluated] = useState(false);
  // MEMO: 初回登録時のコーチング停止のためツアー起動を抑止
  // const { open: openAnnouncementModal, closeModal: closeAnnouncementModal } = useAnnouncement();
  // const openAnnouncementModal = false;

  // MEMO: プロフィール充実促進は一旦無効化
  // const profileCompletionRate = React.useMemo(() => {
  //   if (!user) return null;
  //   return calculateBasicInfoCompletionRateFromUser(user);
  // }, [user]);

  // 「年収450万円未満」もしくは「年齢25歳未満」の場合はポップアップ非表示
  const isSecondOpinionStartupEligible = React.useMemo(() => {
    if (!user) return false;
    if ((user.annual_income ?? 0) < 450) return false;
    if (!user.birthday) return false;

    const birthday = dayjs(user.birthday);
    if (!birthday.isValid()) return false;

    return dayjs().diff(birthday, "year") >= 25;
  }, [user]);

  React.useEffect(() => {
    if (hasEvaluatedStartupPopup.current) return;
    if (!user) return;
    if (isNotificationModalOpen) return;

    hasEvaluatedStartupPopup.current = true;
    setStartupPopupEvaluated(true);

    // 未入力プロフィール（投資経験・金融資産・興味項目）があるユーザーには、
    // セカンドオピニオンより優先して入力必須モーダルを表示する。
    // このモーダルを表示した回はセカンドオピニオンを出さない（次回以降の起動で週1判定に従う）。
    if (shouldShowStartupProfileModal(user)) {
      setIsProfileModalOpen(true);
      return;
    }

    if (StartupPopupManager.shouldShowAtLaunch() && isSecondOpinionStartupEligible) {
      // 自動表示したタイミングで記録し、次回の週1判定に反映する。
      StartupPopupManager.markShownNow();
      setIsSecondOpinionStartupModalOpen(true);
    }
  }, [isNotificationModalOpen, isSecondOpinionStartupEligible, user]);

  // MEMO: MS-INS向けモーダルの初期・定期ポップアップを停止
  // useEffect(() => {
  //   // 送客済みのため、モーダルは表示しない
  //   const permanentlyDismissed = localStorage.getItem("fp_modal_permanently_dismissed");
  //   if (permanentlyDismissed === "true") {
  //     setIsMsinsLeadModalOpen(false);
  //     return;
  //   }

  //   // ツアーやアナウンスの表示中のため、モーダルは表示しない
  //   if (runTour || openAnnouncementModal) {
  //     setIsMsinsLeadModalOpen(false);
  //     return;
  //   }

  //   const lastDismissed = localStorage.getItem("fp_modal_dismissed");
  //   const now = new Date();
  //   if (lastDismissed) {
  //     const diffDays = getDateDiffInDays(new Date(lastDismissed), now);
  //     if (diffDays < 3) {
  //       setIsMsinsLeadModalOpen(false); // まだ3日間経っていない
  //       return;
  //     }
  //   }

  //   // 表示条件を満たすのでモーダル表示
  //   setIsMsinsLeadModalOpen(true);
  // }, [runTour, openAnnouncementModal]);

  // const getDateDiffInDays = (date1: Date, date2: Date) => {
  //   const oneDay = 1000 * 60 * 60 * 24;
  //   const diffTime = date2.getTime() - date1.getTime();
  //   return Math.floor(diffTime / oneDay);
  // };

  const handleLimitedPropertyClick = React.useCallback(
    (property: LimitedPropertyWithAgent) => {
      history.push("/limited_properties/detail", { property });
    },
    [history]
  );

  const handleLimitedPropertiesListClick = React.useCallback(() => {
    history.push("/limited_properties");
  }, [history]);

  // ホームの並びは診断結果と同じ上位順なので、カードの王冠（上位3人）を詳細にも引き継ぐ
  const handleCompatibilityAgentClick = React.useCallback(
    (agentId: number, rank: number) => {
      history.push(`/agent_search/${agentId}?source=${AGENT_DETAIL_URL_SOURCE.TOP}`, { compatibilityRank: rank });
    },
    [history]
  );

  // ホーム起点であることを履歴エントリの state で診断フローへ渡す。
  // 診断フロー側はこの state でステップチップスの有無・ファネルイベント送信・結果画面の出口を切り替える。
  // 診断結果そのものは渡さない（履歴 state はログアウトを跨いで残るため、結果画面が必ずサーバへ取得しにいく）
  const handleInvestmentStyleClick = React.useCallback(
    (isDiagnosed: boolean) => {
      history.push(isDiagnosed ? "/investment_style_diagnosis/result" : "/investment_style_diagnosis/intro", {
        fromHome: true,
      });
    },
    [history]
  );

  // // TODO WANTFIX: we can rewrite the following state checking as for-loop or iterator.
  // // I don't have any idea to pass type check of Typescript with the iterative approach.
  const cashflowHasError =
    loan_loadable.state === "hasError" || property_id_to_this_month_sheet_loadable.state === "hasError";
  const isCashflowLoading =
    loan_loadable.state === "loading" || property_id_to_this_month_sheet_loadable.state === "loading";

  const hasProposalMatchings = proposalMatchings.length > 0;
  const hasOwnedPropertiesReady = backendLoaded && hasOwnedProperties;
  const sectionOrder = buildHomeSectionOrder({
    hasOwnedProperties: hasOwnedPropertiesReady,
    hasProposalMatchings,
  });

  const sectionRenderers: Record<HomeSectionKey, () => JSX.Element | null> = {
    // 投資スタイル診断のミニ表示セクション
    styleDiagnosis: () => {
      // 表示条件は homeSectionVisibility.ts に集約（判定前・判別不能時は非表示）
      if (!canShowStyleDiagnosisSection(investmentStyleStatus)) return null;
      return (
        <Box>
          <InvestmentStyleSummarySection
            diagnosis={investmentStyleDiagnosis}
            onClick={() => handleInvestmentStyleClick(investmentStyleStatus === "diagnosed")}
          />
        </Box>
      );
    },
    // 相性の良いプランナーセクション（登録導線と同じ大判カード）。
    // 投資スタイル診断の状態には依存しない（未診断でも同じカードを出し、診断導線は診断セクションが担う）。
    // マッチ状況の確定前に描画すると、応答後にセクション順が入れ替わって位置が飛ぶため、
    // proposal_matchings の読み込み完了を待ってから表示する
    compatibilityAgents: () => {
      if (proposalMatchingsLoading) return null;
      // 位置が確定した後の取得中はスケルトンで高さを確保し、カードの出現で下のセクションが
      // 押し下げられてタップ位置がずれるのを防ぐ
      if (compatibilityAgentsLoading) {
        return (
          <Box>
            <CompatibilityAgentsSectionSkeleton />
          </Box>
        );
      }
      return compatibilityAgents.length > 0 ? (
        <Box>
          <CompatibilityAgentsSection agents={compatibilityAgents} onClickAgent={handleCompatibilityAgentClick} />
        </Box>
      ) : null;
    },
    // キャッシュフローセクション
    cashflow: () => {
      // 物件を保有しているかどうかの初期値読み込み期間はローディング表示
      if (!backendLoaded) {
        return (
          <Box>
            <Loading />
          </Box>
        );
      }
      // PCは未保有時に登録導線を出し、取得エラーをゼロ収支として表示しない
      if (!hasOwnedProperties) return isMobile ? null : <EmptyCashFlowSection onAddProperty={() => setIsAddPropertyModalOpen(true)} />;
      if (cashflowHasError) return null;
      return (
        <Box>
          {isCashflowLoading ? (
            // 保有物件ありで、データ読み込み中
            <Loading />
          ) : (
            // 保有物件ありで、データ読み込み完了
            <CashFlowSection
              properties={propertiesWithSample}
              loans={loan_loadable.contents}
              propertyIdToThisMonthSheet={property_id_to_this_month_sheet_loadable.contents}
            />
          )}
        </Box>
      );
    },
    // マッチングエージェントセクション
    matchingAgent: () =>
      proposalMatchings.length > 0 ? (
        <Box>
          <MatchingAgentSection proposalMatchings={proposalMatchings} />
        </Box>
      ) : null,
    // お知らせ・キャンペーンセクション
    announcement: () => (
      <Box>
        <AnnouncementSection
          handleOpenModal={handleOpenModal}
          onClickTaxReturnBanner={onClickTaxReturnBanner}
          onClickMsinsBanner={onClickMsinsBanner}
          onClickCampaignAccept={onClickCampaignAccept}
          onClickSecondOpinion={onClickSecondOpinion}
          onClickSmaulBanner={onClickSmaulBanner}
          onClickCozuchiBanner={() => setIsCozuchiModalOpen(true)}
          onClickWebinarBanner={() => {
            const webinar = premiumWebinars[0];
            if (!webinar?.id) {
              history.push("/webinars");
              return;
            }
            history.push(`/webinars/${webinar.id}`);
          }}
        />
      </Box>
    ),
    // プロパリー限定物件セクション
    limitedProperties: () =>
      !limitedLoading && topLimitedProperties.length > 0 ? (
        <Box>
          <LimitedPropertiesSection
            properties={topLimitedProperties}
            onClickProperty={handleLimitedPropertyClick}
            onClickViewAll={handleLimitedPropertiesListClick}
          />
        </Box>
      ) : null,
    // プロパリー限定動画セクション
    premiumWebinar: () =>
      premiumWebinars.length > 0 ? (
        <Box>
          <PremiumWebinarSection webinars={premiumWebinars} loading={premiumWebinarsLoading} />
        </Box>
      ) : null,
    // 学習導線セクション
    learning: () => (
      <Box>
        <LearningSection />
      </Box>
    ),
  };

  // Msins向けリードバナーのクリック時
  const onClickMsinsBanner = async () => {
    sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationMSINS);
    // Msins向けリードモーダルを表示
    setIsMsinsLeadModalOpen(true);
  };

  // Msins向けリードモーダルのクローズ時
  const onCloseMsinsLead = async () => {
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("fp_modal_dismissed", today);
    setIsMsinsLeadModalOpen(false);
  };

  // Msins向けリードモーダルのボタンクリック時
  const onClickMsinsInterest = async () => {
    // Msins向けリードモーダルを非表示
    setIsMsinsLeadModalOpen(false);
    // Msins向け確認モーダルを表示
    setIsMsinsCheckModalOpen(true);
  };

  // Msins向け確認モーダルの承認時
  const onClickAccept = async () => {
    // Msins向けリードを登録し、承認時モーダルを表示
    const result = await postMsinsLead({
      source: MsinsLeadEntrySourceEnum.検討者向けバナー,
    });

    if (result.success) {
      // 承認時モーダルを表示
      setIsApproveModalOpen(true);
    } else {
      // エラー処理
      console.error(result.error);
      enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
    }
    // Msins向け確認モーダルを非表示
    setIsMsinsCheckModalOpen(false);
  };

  const onClickCampaignAccept = async () => {
    if (typeof window !== "undefined") {
      const campaignFormUrl = buildCampaignGoogleFormPrefilledUrl(user);
      window.open(campaignFormUrl, "_blank", "noopener,noreferrer");
    }
  };
  const onClickTaxReturnBanner = React.useCallback(() => {
    sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationTaxReturn);
    history.push("/balance", { initialMode: "tax_return" });
  }, [history]);
  const openSecondOpinionForm = React.useCallback(() => {
    if (typeof window !== "undefined") {
      const secondOpinionFormUrl = buildSecondOpinionGoogleFormPrefilledUrl(user);
      window.open(secondOpinionFormUrl, "_blank", "noopener,noreferrer");
    }
  }, [user]);
  const openSmaulForm = React.useCallback(() => {
    if (typeof window !== "undefined") {
      const smaulFormUrl = buildSmaulGoogleFormPrefilledUrl(user);
      window.open(smaulFormUrl, "_blank", "noopener,noreferrer");
    }
  }, [user]);

  // スクロールバナーのセカンドオピニオンは、起動時と同じモーダルを開く
  const onClickSecondOpinion = React.useCallback(() => {
    sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationSecondOpinion);
    setIsSecondOpinionStartupModalOpen(true);
  }, []);

  const onClickSmaulBanner = React.useCallback(() => {
    sendEventToAppMeasurementTool(AppEventTokenEnum.NotificationSMAUL);
    setIsSmaulStartupModalOpen(true);
  }, []);

  // 自動表示時に markShownNow() で記録済みのため、閉じる操作では頻度記録を行わない。
  // これにより、スクロールバナー経由の手動オープン時に自動表示の枠を消費しない。
  const onCloseSecondOpinionStartupModal = React.useCallback(() => {
    setIsSecondOpinionStartupModalOpen(false);
  }, []);

  const onClickSecondOpinionStartupModalApply = React.useCallback(() => {
    openSecondOpinionForm();
    setIsSecondOpinionStartupModalOpen(false);
  }, [openSecondOpinionForm]);

  // プロフィール入力モーダルの保存。PATCH /v1/user の結果は usePatchUser の state を介して下の useEffect で判定する。
  const onSubmitStartupProfile = React.useCallback(
    (update: StartupProfileUpdate) => {
      if (patchProfileLoading) return;
      setProfileErrorMessage("");
      setPendingProfileUpdate(update);
      setProfileSubmitAttempted(true);
      patchProfileUser(update);
    },
    [patchProfileLoading, patchProfileUser]
  );

  // 保存完了を検知して、成功時のみ Recoil の UserState を同期しモーダルを閉じる。
  React.useEffect(() => {
    if (!profileSubmitAttempted) return;
    if (patchProfileLoading) return;

    setProfileSubmitAttempted(false);

    if (patchProfileResponse && pendingProfileUpdate) {
      // サーバ確定済みの User で同期する（interested_property_type_ids 等の派生項目も含めて整合させる）。
      setUser(patchProfileResponse);
      setIsProfileModalOpen(false);
    } else {
      setProfileErrorMessage(patchProfileError || "保存に失敗しました。時間をおいて再度お試しください。");
    }
  }, [
    profileSubmitAttempted,
    patchProfileLoading,
    patchProfileResponse,
    patchProfileError,
    pendingProfileUpdate,
    setUser,
  ]);

  // マスタ取得不能・保存失敗時のみの脱出口。当セッションは閉じるが、
  // 未入力のままなので次回起動時に再度表示される（頻度記録はしない）。
  const onSkipStartupProfile = React.useCallback(() => {
    setIsProfileModalOpen(false);
  }, []);

  // Msins向けリード承認確認モーダルのクローズ時
  const onCloseAccept = async () => {
    localStorage.setItem("fp_modal_permanently_dismissed", "true");
    setIsApproveModalOpen(false);
  };

  const { left: pcLeftSections, right: pcRightSections } = splitHomeSectionOrderForPc(sectionOrder);
  const spFollowingSections = isMobile ? sectionOrder.filter(key => key !== "styleDiagnosis").map(key => ({ key, node: sectionRenderers[key]() })).filter(section => section.node !== null) : [];

  return (
    <BaseTemplate
      headerTitle={isMobile ? "ホーム" : undefined}
      navigationPath={NavigationPath.HOME}
      isPcV2
      isDense
      HeaderActionComponent={
        isMobile ? (
          <IconButton className={classes.icon} onClick={() => history.push("/settings")}>
            <SettingsOutlined />
          </IconButton>
        ) : undefined
      }
    >
      <HomeTour
        accountId={user?.id}
        isMobile={isMobile}
        backendLoaded={backendLoaded}
        startupPopupEvaluated={startupPopupEvaluated}
        notificationPermissionChecking={isNotificationPermissionChecking}
        investmentStyleLoading={investmentStyleResponse.status === "loading"}
        cashflowLoading={hasOwnedProperties && isCashflowLoading}
        blockingDialogOpen={isProfileModalOpen || isNotificationModalOpen || isSecondOpinionStartupModalOpen ||
          isAddPropertyModalOpen || isMsinsLeadModalOpen || isMsinsCheckModalOpen || isApproveModalOpen ||
          isSmaulStartupModalOpen || isCozuchiModalOpen}
      />
      <Box className={classes.sectionStack}>
        {/* MEMO: プロフィール充実促進は一旦無効化 */}
        {/* {profileCompletionRate === null || profileCompletionRate < 100 ? (
          <Box className={classes.profileBanner} onClick={() => history.push("/settings", { initialSetting: "basic" })}>
            <Box className={classes.profileBannerLeft}>
              <img className={classes.editProfileIcon} src={EditProfileIcon} />
              <Box className={classes.profileBannerTexts}>
                <Typography className={classes.profileBannerTextPrimary} variant="body2">
                  プロフィールを充実させましょう
                </Typography>
                <Typography className={classes.profileBannerTextSecondary} variant="h6">
                  より精度の高いメッセージが届きます
                </Typography>
              </Box>
            </Box>
            <Box className={classes.profileBannerAction}>設定する</Box>
          </Box>
        ) : null} */}

        {/* アンケート誘導バナーコンポーネント - 現在使われていないが、再度アンケートを表示する可能性があるため、コードは残しておく。不要であれば削除する。 */}
        {/* 実体はコミット 2bcb4a8fc で削除済み（旧パス: src/pages/renewal_v1/mypage/questionnaire_bannar.tsx）。復活させる場合は同コミットの親から取り出す */}
        {/* <Box mb={1}>
        <QuestionnaireBannar />
        </Box> */}

        {/* 条件に応じて適切にセクションを並べ替えて表示する */}
        {/* PC は Figma の BaseTemplate に合わせて左メインカラム + 右レールの2カラム表示にする */}
        {isMobile ? (
          <HomeMobileSections styleDiagnosis={sectionRenderers.styleDiagnosis()} sections={spFollowingSections} />
        ) : (
          <Box className={classes.pcColumns}>
            <Box className={classes.pcMainColumn}>
              {pcLeftSections.map((sectionKey) => (
                <React.Fragment key={sectionKey}>{sectionRenderers[sectionKey]()}</React.Fragment>
              ))}
              <NewNotificationSection />
            </Box>
            <Box className={classes.pcRightRail}>
              {pcRightSections.map((sectionKey) => (
                <React.Fragment key={sectionKey}>
                  {sectionRenderers[sectionKey]()}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}

        {/* お知らせセクション内に移動したためコメントアウト */}
        {/* 実体はコミット 2bcb4a8fc で削除済み（旧パス: src/pages/renewal_v1/mypage/tax_return_section.tsx）。復活させる場合は同コミットの親から取り出す */}
        {/* 確定申告サポート機能セクション */}
        {/* <Box mt={1} mb={1}>
        <TaxReturnSection />
        </Box> */}

        {/* MEMO: オファーに未読バッジが存在するため、非表示にするよう修正 */}
        {/* 未読オファー通知チップ - 表示条件: 未読のオファーがある場合 */}
        {/* {hasUnreadValue && (
        <Box className={classes.chipWrapper}>
          <Chip
            className={classes.chip}
            icon={<MailIcon className={classes.chipIcon} />}
            label="未読の新規オファーがあります"
            onClick={() => history.push("/offer")}
          />
        </Box>
        )} */}

        {/* MEMO: トップ画面での表示は一旦削除*/}
        {/* 実体はコミット 2bcb4a8fc で削除済み（旧パス: src/pages/renewal_v1/mypage/register_property_section.tsx）。復活させる場合は同コミットの親から取り出す */}
        {/* 物件登録セクション - 表示条件: 物件を保有していない場合 */}
        {/* {!hasOwnedProperties && (
        <Box mt={1}>
          <RegisterPropertySection />
        </Box>
        )} */}

        {/* 学習導線セクションがあるため、非表示 */}
        {/* 実体はコミット 2bcb4a8fc で削除済み（旧パス: src/pages/renewal_v1/mypage/journal_section.tsx）。復活させる場合は同コミットの親から取り出す */}
        {/* 不動産投資の勉強コラム(ジャーナル)セクション */}
        {/* <Box mt={1}>
        <JournalSection />
        </Box> */}

        {/* 他社サービスとコラボを行なった際のバナー表示過去実装 */}
        {/* {isMobile ? (
        <Box mt={1} position="relative">
          <a href="https://dribda.com/link.php?i=phy3zpexb2lo&m=mhy0ed110nik" target="_blank" rel="noopener noreferrer">
            <img className={classes.image} src={CozuchiBanner} alt="Cozuchi banner" />
          </a>
        </Box>
        ) : (
          showBanner && (
            <Box className={classes.floatingBanner}>
              <IconButton className={classes.closeButton} onClick={() => setShowBanner(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
              <a
                href="https://dribda.com/link.php?i=phy3zpexb2lo&m=mhy0ed110nik"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img className={classes.image} src={CozuchiBanner} alt="Cozuchi banner" />
              </a>
            </Box>
          )
        )} */}
      </Box>

      <AddNewPropertyModal
        isOpen={isAddPropertyModalOpen}
        onClose={() => setIsAddPropertyModalOpen(false)}
        includeConsideration
      />
      {isProfileModalOpen && (
        <StartupProfileModal
          open={isProfileModalOpen}
          missingFields={missingStartupProfileFields}
          submitting={patchProfileLoading}
          errorMessage={profileErrorMessage}
          onSubmit={onSubmitStartupProfile}
          onSkip={onSkipStartupProfile}
        />
      )}
      <SecondOpinionStartupModal
        open={isSecondOpinionStartupModalOpen}
        onClose={onCloseSecondOpinionStartupModal}
        onApply={onClickSecondOpinionStartupModalApply}
      />
      <SmaulStartupModal
        open={isSmaulStartupModalOpen}
        onClose={() => setIsSmaulStartupModalOpen(false)}
        onApply={() => {
          openSmaulForm();
          setIsSmaulStartupModalOpen(false);
        }}
      />

      <NotificationPermissionModal
        open={isNotificationModalOpen}
        onAllow={requestNotificationPermission}
        onDismiss={closeNotificationPermissionModal}
        loading={isRequestingNotificationPermission}
      />
      <CozuchiModal open={isCozuchiModalOpen} onClose={() => setIsCozuchiModalOpen(false)} user={user} />

      {/* MEMO: 初回登録時機能アップデート表示停止のためアナウンスモーダルを一時的に無効化 */}
      {/* <AnnouncementModal open={openAnnouncementModal} onClose={closeAnnouncementModal} /> */}
      {isMsinsLeadModalOpen && <MsinsLeadModal onClose={onCloseMsinsLead} onAccept={() => onClickMsinsInterest()} />}
      {isMsinsCheckModalOpen && (
        <LeadCheckModal onClose={() => setIsMsinsCheckModalOpen(false)} onAccept={() => onClickAccept()} />
      )}
      {isApproveModalOpen && <OfferApproveModal offerType="msins_consideration" onClose={onCloseAccept} />}
    </BaseTemplate>
  );
};
